import React, { useMemo } from "react";
import WidgetCard from "./WidgetCard";
import { Row, Col, Select } from "antd";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { COLORS } from "@ts/constants";
import { keys, reduce, includes } from "underscore";
import { IProductRatingVsCompetitorsWidgetProps } from "@ts/common-types";

const ProductRatingVsCompetitorsWidget: React.FC<IProductRatingVsCompetitorsWidgetProps> = ({
    title,
    widgetData,
    selectedRatingFilter,
    onProductRatingVsCompetitorsFilterChange,
    productRatingVsCompetitorsFilterOptions
}) => {
    const chartOptions = useMemo(() => {
        const { trendChart, spiderChart } = widgetData;
        const rankChartOptions = {
            chart: {
                type: "column",
            },
            title: {
                text: "",
                margin: 50,
                style: {
                    fontSize: "14px",
                    fontWeight: "normal",
                },
            },
            colors: COLORS,
            xAxis: {
                categories: trendChart?.categories,
                title: {
                    text: "",
                },
            },
            yAxis: {
                min: 0,
                title: {
                    text: "#Rating",
                },
            },
            series: trendChart?.series,
            tooltip: {
                formatter: function(this: Highcharts.TooltipFormatterContextObject): string {
                    const rating: number = (this.point.options as any).rating;
                    return `${this.series.name}: ${this.y}<br>Rating: ${rating}`;
                }
            },
            credits: {
                enabled: false,
            },
        };

        const spiderChartOptions = {
            chart: {
                polar: true,
                type: "line",
            },
            title: {
                text: "",
                align: "center",
            },
            pane: {
                size: "70%",
            },
            xAxis: {
                categories: spiderChart?.["Features"],
                tickmarkPlacement: "on",
                lineWidth: 0,
            },
            yAxis: {
                gridLineInterpolation: "polygon",
                lineWidth: 0,
                min: 0,
                max: 5,
                tickInterval: 0.5,
                title: {
                    text: "Score",
                },
            },
            tooltip: {
                shared: true,
                pointFormat:
                    '<span style="color:{series.color}">{series.name}: <b>{point.y}</b><br/>',
            },
            credits: {
                enabled: false,
            },
            legend: {
                align: "right",
                verticalAlign: "middle",
                layout: "vertical",
            },
            series: [
                {
                    name: "Category Average",
                    type: "area",
                    data: spiderChart?.["Category Average"],
                    pointPlacement: "on",
                    color: "rgba(207,246,223,0.9)",
                },
                {
                    name: "My SKU Score",
                    data: spiderChart?.["My SKU Score"],
                    pointPlacement: "on",
                    color: "#1E90FF",
                },
                ...reduce(
                    keys(spiderChart),
                    (acc, sku, index) => {
                        if (!includes(["Category Average", "My SKU Score", "Features"], sku)) {
                            acc.push({
                                name: sku,
                                data: spiderChart[sku],
                                pointPlacement: "on",
                                color: COLORS[index + 1],
                            });
                        }
                        return acc;
                    },
                    []
                )
            ],
        };

        return {
            rankChartOptions,
            spiderChartOptions,
        };
    }, [widgetData]);

    return (
        <WidgetCard className="my-4">
            <Row className="mb-3" justify="space-between" align="middle">
                <Col>
                    <h5>{title || "Product #Rating vs Competitors"}</h5>
                </Col>
                <Col>
                    <label style={{ marginRight: 8 }}>Compare with:</label>
                    <Select
                        mode="multiple"
                        value={selectedRatingFilter}
                        options={ productRatingVsCompetitorsFilterOptions }
                        onChange={onProductRatingVsCompetitorsFilterChange}
                        maxTagCount={1}
                        maxTagTextLength={10}
                        allowClear
                        style={{ width:220 }}
                    />
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={chartOptions?.rankChartOptions}
                    />
                </Col>
                <Col span={12}>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={chartOptions?.spiderChartOptions}
                    />
                </Col>
            </Row>
        </WidgetCard>
    );
};

export default ProductRatingVsCompetitorsWidget;